// src/hooks/useFeatureAccess.ts
//
// Reads custom claims from the Firebase Auth ID token and exposes
// per-feature access flags. Claims are server-set via the Admin SDK
// (see scripts/manageFeatureAccess.mjs); the client can only
// read them, never write them. The cryptographic signature on the
// JWT means we can trust the values without an extra Firestore
// read.
//
// Custom claims are written under a `features` namespace:
//
//   { features: { memoryLog: true, admin: false }, ... }
//
// This namespace keeps Firebase's reserved claims (sub, iat, exp,
// auth_time, etc.) separate from our own access flags and gives us
// room to add more flags later without worrying about name
// collisions.
//
// Usage:
//
//   const { features, isLoading } = useFeatureAccess();
//   if (features.memoryLog) { ... }
//
// Refresh semantics: on auth state change we forcibly fetch a new
// token (`getIdTokenResult(true)`) so claims granted/revoked
// server-side after the user's last sign-in are picked up
// immediately. Without the force flag, claims update only when
// the cached token expires (~1 hour) which makes "I gave you
// access, sign in to use it" feel sluggish. The cost is one extra
// network round-trip per auth state change, which is rare.
//
// Server-side enforcement still happens in firestore.rules
// (request.auth.token.features.<flag> == true). The hook only
// drives UI gating; it is not a security boundary on its own.

import { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../services/firebase';

/** Feature flags surfaced through the JWT custom-claims path. Add
 *  new flags here as we add new internal/beta surfaces. Keep this
 *  in sync with KNOWN_FEATURES in scripts/manageFeatureAccess.mjs. */
export type FeatureFlag = 'memoryLog' | 'admin';

const ALL_FLAGS: FeatureFlag[] = ['memoryLog', 'admin'];

const EMPTY: Record<FeatureFlag, boolean> = {
  memoryLog: false,
  admin: false,
};

export interface FeatureAccessResult {
  features: Record<FeatureFlag, boolean>;
  /** True until the first auth state has been observed and its
   *  token resolved. Use this to avoid flashing gated UI on mount. */
  isLoading: boolean;
}

export function useFeatureAccess(): FeatureAccessResult {
  const [features, setFeatures] = useState<Record<FeatureFlag, boolean>>(EMPTY);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        if (!cancelled) {
          setFeatures(EMPTY);
          setIsLoading(false);
        }
        return;
      }
      try {
        // Force-refresh the token so we see claims that were
        // granted/revoked since the last sign-in. The token is
        // cached for ~1h by default; without the `true` we'd
        // require sign-out to pick up new claims.
        const tokenResult = await user.getIdTokenResult(true);
        if (cancelled) return;
        const claimFeatures = (tokenResult.claims.features || {}) as Record<
          string,
          boolean
        >;
        const next: Record<FeatureFlag, boolean> = { ...EMPTY };
        for (const f of ALL_FLAGS) {
          next[f] = !!claimFeatures[f];
        }
        setFeatures(next);
      } catch (err) {
        // If the token refresh fails (network blip), fall back to
        // empty rather than leaving stale values. Failing closed is
        // the right default — we'd rather hide a feature than
        // expose one to someone who shouldn't have it.
        console.warn('useFeatureAccess: token refresh failed', err);
        if (!cancelled) setFeatures(EMPTY);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  return { features, isLoading };
}
